/**
 * Purpose: Manages turn rotation, seat advancement, class graduation, and win detection.
 * Owns: Turn state machine, active player tracking, round progression, correct/wrong
 *       answer processing, class graduation logic, game completion checks.
 * Notes: Redesigned for the "School Day" classroom system. The turn flow is:
 *        1. Question asked (from current class subject)
 *        2. If correct: roll dice, advance seats, check graduation, check finish
 *        3. If wrong: stay on seat, end turn
 *        4. Advance to next player (in solo mode, loop back to same player)
 *        5. Skip finished players
 */

import type { GameState, Player, PlayerClassProgress } from '../types/game';
import { createEmptyTurn } from './gameState';
import { advanceSeats, getCurrentClassPeriod, getQuestionsPerPeriod } from './seatProgression';
import { calculateLetterGrade, calculateGPA } from './scoring';
import { TOTAL_PERIODS } from '../config/classConfig';

// _______________________________________________
//              TURN ADVANCEMENT
// _______________________________________________

/**
 * Advance to the next player's turn.
 * Skips players who have already finished. In solo mode (1 player),
 * loops back to the same player. When the last player finishes a round,
 * increments the round counter and wraps to the first active player.
 */
export function advanceToNextTurn(state: GameState): GameState {
  const playerCount = state.players.length;
  if (playerCount === 0) return state;

  /* Find the next player who hasn't finished */
  let nextIndex = state.currentPlayerIndex;
  let attempts = 0;

  do {
    nextIndex = (nextIndex + 1) % playerCount;
    attempts++;
    /* Safety: if all players are finished, don't loop forever */
    if (attempts > playerCount) {
      return { ...state, gamePhase: 'results' };
    }
  } while (state.players[nextIndex].finished);

  const isNewRound = nextIndex <= state.currentPlayerIndex;

  /* Pure Classroom skips the dice roll — go directly to question phase */
  const nextPhase = state.gameMode === 'pureClassroom' ? 'question' : 'rolling';

  return {
    ...state,
    currentPlayerIndex: nextIndex,
    currentRound: isNewRound ? state.currentRound + 1 : state.currentRound,
    currentTurn: createEmptyTurn(state.players[nextIndex].id),
    diceValue: null,
    gamePhase: nextPhase,
    currentQuestion: null,
    hostReaction: null,
  };
}

// _______________________________________________
//             ACTIVE PLAYER HELPERS
// _______________________________________________

/** Get the player whose turn it currently is. */
export function getActivePlayer(state: GameState): Player | null {
  return state.players[state.currentPlayerIndex] ?? null;
}

/** Get the player who will go next (for turn-order preview). */
export function getNextPlayer(state: GameState): Player | null {
  const playerCount = state.players.length;
  if (playerCount === 0) return null;

  let nextIndex = state.currentPlayerIndex;
  let attempts = 0;

  do {
    nextIndex = (nextIndex + 1) % playerCount;
    attempts++;
    if (attempts > playerCount) return null;
  } while (state.players[nextIndex].finished);

  return state.players[nextIndex] ?? null;
}

/** Start the game — transition from setup to the first player's turn phase. */
export function startGame(state: GameState): GameState {
  if (state.players.length === 0) return state;

  /* Pure Classroom skips dice rolling — start directly in question phase */
  const startPhase = state.gameMode === 'pureClassroom' ? 'question' : 'rolling';

  return {
    ...state,
    gamePhase: startPhase,
    currentPlayerIndex: 0,
    currentRound: 1,
    currentTurn: createEmptyTurn(state.players[0].id),
    diceValue: null,
    currentQuestion: null,
    hostReaction: null,
  };
}

// _______________________________________________
//              CORRECT ANSWER PROCESSING
// _______________________________________________

/**
 * Process a correct answer: advance seats in the current class,
 * check for class graduation, update class progress, check for finish.
 * Returns updated player with all progression applied.
 */
export function processCorrectAnswer(
  player: Player,
  diceRoll: number,
): Player {
  const currentPeriodIndex = player.currentPeriod - 1;
  const currentClass = getCurrentClassPeriod(player);
  const currentProgress = player.classProgress[currentPeriodIndex];

  /* Advance seats based on dice roll */
  const { newSeat, graduated } = advanceSeats(
    currentProgress.currentSeat,
    diceRoll,
    currentClass.classSize.seats,
  );

  /* Update class progress for the current period */
  const updatedProgress: PlayerClassProgress = {
    ...currentProgress,
    currentSeat: newSeat,
    questionsAnswered: currentProgress.questionsAnswered + 1,
    questionsCorrect: currentProgress.questionsCorrect + 1,
    passed: graduated,
    letterGrade: graduated
      ? calculateLetterGrade(
          currentProgress.questionsAnswered + 1,
          currentProgress.questionsCorrect + 1,
          currentProgress.questionsWithHelp,
          currentProgress.behaviorDock,
          player.ageGroup,
        )
      : null,
  };

  /* Build updated class progress array */
  const updatedClassProgress = [...player.classProgress];
  updatedClassProgress[currentPeriodIndex] = updatedProgress;

  /* Determine new period if graduated */
  let newPeriod = player.currentPeriod;
  let isFinished = player.finished;

  if (graduated) {
    if (player.currentPeriod >= TOTAL_PERIODS) {
      /* Graduated from Final Exam — player is done */
      isFinished = true;
    } else {
      /* Move to the next period */
      newPeriod = player.currentPeriod + 1;
    }
  }

  /* Calculate updated GPA from all passed classes */
  const newGpa = calculateGPA(updatedClassProgress);

  /* Update overall stats */
  const newStreak = player.currentStreak + 1;

  return {
    ...player,
    currentPeriod: newPeriod,
    classProgress: updatedClassProgress,
    gpa: newGpa,
    finished: isFinished,
    score: player.score + diceRoll,
    questionsCorrect: player.questionsCorrect + 1,
    currentStreak: newStreak,
    bestStreak: Math.max(player.bestStreak, newStreak),
  };
}

// _______________________________________________
//              WRONG ANSWER PROCESSING
// _______________________________________________

/**
 * Process a wrong answer: stay on current seat, record the wrong answer,
 * reset streak, end turn.
 */
export function processWrongAnswer(player: Player): Player {
  const currentPeriodIndex = player.currentPeriod - 1;
  const currentProgress = player.classProgress[currentPeriodIndex];

  /* Update class progress — increment questions answered but not correct */
  const updatedProgress: PlayerClassProgress = {
    ...currentProgress,
    questionsAnswered: currentProgress.questionsAnswered + 1,
  };

  const updatedClassProgress = [...player.classProgress];
  updatedClassProgress[currentPeriodIndex] = updatedProgress;

  return {
    ...player,
    classProgress: updatedClassProgress,
    questionsWrong: player.questionsWrong + 1,
    currentStreak: 0,
  };
}

// _______________________________________________
//              PURE CLASSROOM ANSWER PROCESSING
// _______________________________________________
// In Pure Classroom mode there are no dice or seats. Each period has a fixed
// number of questions. Every answer (correct or wrong) advances the question
// count. When all questions in a period are answered, the player is graded
// and moves to the next period.

/**
 * Process an answer in Pure Classroom mode.
 * No dice, no seat advancement — just count questions and grade on completion.
 * Both correct AND wrong answers advance progress (everyone finishes every period).
 */
export function processPureClassroomAnswer(
  player: Player,
  isCorrect: boolean,
): Player {
  const currentPeriodIndex = player.currentPeriod - 1;
  const currentClass = getCurrentClassPeriod(player);
  const currentProgress = player.classProgress[currentPeriodIndex];
  const totalQuestions = getQuestionsPerPeriod(currentClass);

  const newQuestionsAnswered = currentProgress.questionsAnswered + 1;
  const newQuestionsCorrect = currentProgress.questionsCorrect + (isCorrect ? 1 : 0);

  /* Check if this was the last question in the period */
  const periodComplete = newQuestionsAnswered >= totalQuestions;

  /* Update class progress for the current period */
  const updatedProgress: PlayerClassProgress = {
    ...currentProgress,
    questionsAnswered: newQuestionsAnswered,
    questionsCorrect: newQuestionsCorrect,
    passed: periodComplete,
    letterGrade: periodComplete
      ? calculateLetterGrade(
          newQuestionsAnswered,
          newQuestionsCorrect,
          currentProgress.questionsWithHelp,
          currentProgress.behaviorDock,
          player.ageGroup,
        )
      : null,
  };

  /* Build updated class progress array */
  const updatedClassProgress = [...player.classProgress];
  updatedClassProgress[currentPeriodIndex] = updatedProgress;

  /* Determine new period if class is complete */
  let newPeriod = player.currentPeriod;
  let isFinished = player.finished;

  if (periodComplete) {
    if (player.currentPeriod >= TOTAL_PERIODS) {
      /* Completed Final Exam — player is done */
      isFinished = true;
    } else {
      /* Move to the next period */
      newPeriod = player.currentPeriod + 1;
    }
  }

  /* Calculate updated GPA from all passed classes */
  const newGpa = calculateGPA(updatedClassProgress);

  /* Update streaks and overall stats */
  const newStreak = isCorrect ? player.currentStreak + 1 : 0;

  return {
    ...player,
    currentPeriod: newPeriod,
    classProgress: updatedClassProgress,
    gpa: newGpa,
    finished: isFinished,
    score: player.score, /* No dice points in Pure Classroom */
    questionsCorrect: player.questionsCorrect + (isCorrect ? 1 : 0),
    questionsWrong: player.questionsWrong + (isCorrect ? 0 : 1),
    currentStreak: newStreak,
    bestStreak: Math.max(player.bestStreak, newStreak),
  };
}

// _______________________________________________
//              WIN CONDITION CHECKS
// _______________________________________________

/**
 * Check win conditions based on game mode.
 *
 * Speed Run: first player where finished === true wins immediately.
 * Honor Roll / Pure Classroom: when ALL players have finished, highest GPA wins.
 * Solo Speed Run: player finishes = win.
 * Solo Honor Roll / Pure Classroom: player finishes, results show GPA.
 *
 * Returns the winning player, or null if the game is still in progress.
 */
export function checkWinCondition(state: GameState): Player | null {
  const { players, gameMode } = state;

  if (gameMode === 'speedRun') {
    /* Speed Run: first player to finish wins */
    const finishedPlayer = players.find((p) => p.finished);
    return finishedPlayer ?? null;
  }

  /* Honor Roll and Pure Classroom: all players must finish, then compare GPAs */
  const allFinished = players.every((p) => p.finished);
  if (!allFinished) return null;

  const isSolo = players.length === 1;

  if (isSolo) {
    /* Solo: player always "wins" when finished, results show GPA */
    return players[0];
  }

  /* Multi-player: highest GPA wins. Break ties by score. */
  const sorted = [...players].sort((a, b) => {
    if (b.gpa !== a.gpa) return b.gpa - a.gpa;
    return b.score - a.score;
  });

  return sorted[0] ?? null;
}

/**
 * Check if the game should end.
 * Returns true if a win condition has been met.
 */
export function isGameOver(state: GameState): boolean {
  return checkWinCondition(state) !== null;
}
